<template>
  <div class="ai-material-audit-form">
    <div class="operation">
      <p>全方位查看素材信息，AI分析报告并执行审核操作</p>
      <div class="btns">
        <slot v-if="modalStates.isAudit" name="btns" :form="analysisForm"></slot>
      </div>
    </div>

    <div class="main-content" v-if="analysisForm">
      <div class="left-aside">
        <div class="video-display-box" style="text-align: center">
          <video ref="videoRef" controls preload="none" muted :poster="analysisForm.coverAddr" :src="analysisForm.addr"></video>
        </div>
        <div class="video-details">
          <el-card class="box-card" shadow="never">
            <div slot="header" class="clearfix">
              <h3>基础信息</h3>
            </div>
            <div class="details">
              <LabelRow label-width="80px" label="任务ID：">
                <span>{{ analysisForm.id }}</span>
              </LabelRow>
              <LabelRow label-width="80px" label="视频标题：">
                <span class="label-content" :title="analysisForm.imageName">{{ analysisForm.imageName }}</span>
              </LabelRow>
              <LabelRow label-width="80px" label="推送时间：">
                <span class="label-content">{{ moment(analysisForm.createTime).format('YYYY-MM-DD HH:mm:ss') }}</span>
              </LabelRow>
              <LabelRow label-width="80px" label="截止时间：">
                <span class="label-content">{{ getDeadlineTimeStr() }}</span>
              </LabelRow>
              <LabelRow label-width="80px" label="审核状态：">
                <el-tag>{{ AI_AUDIT_LIST.find((i) => i.id === analysisForm.auditStatus)?.name || '-' }}</el-tag>
              </LabelRow>
              <!-- <el-divider></el-divider> -->
              <LabelRow label-width="80px" label="时长：">
                <span>{{ analysisForm.duration }}s</span>
              </LabelRow>
              <!-- 字节转换成MB -->
              <LabelRow label-width="80px" label="大小：">
                <span>{{ (analysisForm.size / 1024 / 1024).toFixed(2) }}MB</span>
              </LabelRow>

              <LabelRow label-width="80px" label="分辨率：">
                <span>{{ analysisForm.rate }}</span>
              </LabelRow>

              <LabelRow label-width="80px" label="上传者：">
                <span>{{ analysisForm.uploadOpName }}</span>
              </LabelRow>
            </div>
          </el-card>
        </div>
      </div>
      <div class="right-aside">
        <el-tabs v-model="materialReportActiveName">
          <el-tab-pane label="AI内容解析" name="2">
            <MaterialContent
              v-if="analysisForm.analysisStatus === ANALYSIS_STATES.ANALYSIS_PASSED"
              ref="materialContentRef"
              :form="analysisForm"
              @update:modified="$emit('update:modified', $event)"
              @subtitle-updated="onMaterialContentSubtitleUpdated"
              @video-desc-tab-change="onVideoDescTabChange"
            />
            <span v-else>暂无解析结果</span>
          </el-tab-pane>

          <el-tab-pane label="AI洞察分析" name="3">
            <InsightReport :form="estimateForm" />
          </el-tab-pane>
          <el-tab-pane label="AI合规报告" name="1">
            <ComplianceReport :form="complianceForm" @update-video-currtime="onVideoCurrtimeChange" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
    <div class="loading-area" v-else v-loading="true"></div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import moment from 'moment'
import { timeStringToSeconds } from '@/common/js/utils.js'
import LabelRow from '@/views/create/bytedance/components/layout/label-row.vue'
import MaterialContent from './material-content.vue'
import ComplianceReport from './material-compliance-report.vue'
import InsightReport from './material-insight-report.vue'
import { AI_AUDIT_LIST, AI_MARTERIAL_ANALYSIS_STATES_ENUM as ANALYSIS_STATES } from '../../consts'

// eslint-disable-next-line
const emits = defineEmits(['subtitle-updated'])
const analysisForm = ref(null) // 素材分析详情
const complianceForm = ref(null) // 合规报告
const estimateForm = ref(null) // 洞察分析
const materialContentRef = ref(null)
const videoRef = ref(null)
const modalStates = computed(() => ({ isAudit: !analysisForm.value?.isEdit, isEdit: analysisForm.value?.isEdit }))
const materialReportActiveName = ref('2')

/**
 * @description: 处理MaterialContent组件的字幕更新事件
 * @param {*} subtitles
 * @return {*}
 */
function onMaterialContentSubtitleUpdated(subtitles) {
  emits('subtitle-updated', subtitles)
}

/**
 * @description: 视频描述文案合并展示
 * @param {*} args
 * @return {*}
 */
function mergeAnalysisText(args) {
  const overall_analysis_text = []
  const { content_narrative_focus, emotion_atmosphere, key_elements, visual_core, timestamp } = args
  if (timestamp) overall_analysis_text.push(`时间戳：${timestamp}`)
  if (visual_core) overall_analysis_text.push(`画面描述：${visual_core}`)
  if (emotion_atmosphere) overall_analysis_text.push(`内容标签：${emotion_atmosphere}`)
  if (key_elements) overall_analysis_text.push(`关键元素：${key_elements}`)
  if (content_narrative_focus) overall_analysis_text.push(`叙事重点：${content_narrative_focus}`)
  return overall_analysis_text.join('\n')
}

/**
 * @description: 格式化截止时间
 * @return {*}
 */
function getDeadlineTimeStr() {
  const { auditDeadlineTime, deadlineHourText } = analysisForm.value
  const isValidTime = moment(auditDeadlineTime).isValid()
  if (auditDeadlineTime && isValidTime) {
    return moment(auditDeadlineTime).format('YYYY-MM-DD HH:mm:ss') + ` ${deadlineHourText}`
  }
  return '-'
}

/**
 * @description: 视频播放控制的公共方法
 * @param {number} startTime - 开始时间（秒）
 * @param {number} endTime - 结束时间（秒，可选）
 * @return {*}
 */
async function playVideoAtTime(startTime, endTime = null) {
  if (!videoRef.value) return

  try {
    // 先暂停当前播放
    if (!videoRef.value.paused) {
      videoRef.value.pause()
    }

    // 设置视频开始时间
    videoRef.value.currentTime = startTime

    // 移除之前的timeupdate监听器
    const oldHandler = videoRef.value._timeUpdateHandler
    if (oldHandler) {
      videoRef.value.removeEventListener('timeupdate', oldHandler)
    }

    // 如果有结束时间，添加监听器在指定时间暂停
    if (endTime !== null && endTime > startTime) {
      const timeUpdateHandler = () => {
        if (videoRef.value.currentTime >= endTime) {
          videoRef.value.pause()
          videoRef.value.removeEventListener('timeupdate', timeUpdateHandler)
        }
      }
      videoRef.value._timeUpdateHandler = timeUpdateHandler
      videoRef.value.addEventListener('timeupdate', timeUpdateHandler)
    }

    // 等待currentTime设置完成后再播放
    await nextTick()
    await videoRef.value.play()
  } catch (err) {
    console.log('视频播放失败:', err)
  }
}

/**
 * @description: 把视频定位到时间戳位置并开始播放
 * @param {number} sec - 时间戳（秒）
 * @return {*}
 */
async function onVideoCurrtimeChange({ startTime, endTime }) {
  if (typeof startTime !== 'number' || startTime < 0 || typeof endTime !== 'number' || endTime < 0) {
    return
  }

  await playVideoAtTime(startTime, endTime)
}

/**
 * @description: 切换视频描述文案tab时，视频定位到时间戳位置
 * @return {*}
 */
async function onVideoDescTabChange(index) {
  if (index === 0) return
  if (!videoRef.value) return

  const { timeline_events } = analysisForm.value.analysisTaskResult.video_description
  const { timestamp } = timeline_events[index - 1] || {}

  if (timestamp) {
    const [startTime, endTime] = timestamp.split('-').map(timeStringToSeconds)
    if ((startTime || startTime === 0) && endTime) {
      console.log('播放时间段:', startTime, '-', endTime)
      await playVideoAtTime(startTime, endTime)
    }
  }
}

// eslint-disable-next-line
defineExpose({
  videoAutoPlay() {
    nextTick(() => {
      videoRef.value.play()
    })
  },
  setForm(row) {
    const auditTaskResult = row.auditTaskResult ? JSON.parse(row.auditTaskResult) : {}
    const analysisTaskResult = row.analysisTaskResult ? JSON.parse(row.analysisTaskResult) : {}
    const estimateTaskResult = row.estimateTaskResult ? JSON.parse(row.estimateTaskResult) : {}
    console.log('analysisTaskResult output: ', analysisTaskResult)
    console.log('estimateTaskResult output: ', estimateTaskResult)
    console.log('auditTaskResult output: ', auditTaskResult)

    // analysisForm.value = analysisTaskResult // 素材分析详情
    analysisForm.value = { ...row, analysisTaskResult } // 素材分析详情
    complianceForm.value = auditTaskResult // 合规报告
    estimateForm.value = estimateTaskResult // 洞察分析
    if (analysisTaskResult?.video_description) {
      const { overall_analysis, timeline_events } = analysisTaskResult.video_description
      analysisTaskResult.video_description = {
        ...analysisTaskResult.video_description,
        overall_analysis_text: mergeAnalysisText(overall_analysis),
        timeline_events: timeline_events.map((i) => ({ ...i, timeline_events_text: mergeAnalysisText(i) }))
      }
    }

    if (analysisTaskResult.suggested_tags?.length) {
      analysisTaskResult.suggested_tags = analysisTaskResult.suggested_tags.map((tag) => {
        // 只显示最后一个标签
        const [lastTag] = tag.name?.split('\\')?.slice(-1) || []
        return { ...tag, lastTag: lastTag || '-' }
      })
    }

    const { subtitles, analysisStatus } = analysisForm.value
    if (subtitles && analysisStatus === ANALYSIS_STATES.ANALYSIS_PASSED) {
      nextTick(() => {
        materialContentRef.value.initAuditFormSubtitles(subtitles)
      })
    }
  },
  getForm() {
    return analysisForm.value
  }
})
</script>

<style scoped lang="scss">
.ai-material-audit-form {
  .operation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }
}
.loading-area {
  min-height: 400px;
}

.main-content {
  position: relative;
  .left-aside {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 1;
    width: 400px;
    .video-display-box {
      height: 225px;
      font-size: 0;
      border-radius: 4px;
      overflow: hidden;
      background-color: #eee;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

      video {
        height: 100%;
        width: 100%;
      }
    }

    .video-details {
      margin-top: 20px;

      ::v-deep .el-card__header {
        padding: 10px 20px;
        background-color: #fbfbfb;
      }

      .details {
        .label-content {
          display: block;
          width: 270px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
  .right-aside {
    margin-left: 420px;
    max-height: 613px;
    min-height: 613px;
    overflow: auto;
    padding: 20px;
    border-radius: 4px;
    border: 1px solid #e6ebf5;
  }
}

.subtitle-inputs {
  max-height: 220px;
  overflow: auto;
  .subtitle-group {
    margin-bottom: 12px;
    position: relative;

    .active {
      background: #d7f2ff;
    }

    .timestamps {
      color: #b5b5b5;
    }

    .cut-icon {
      position: absolute;
      right: 10px;
      top: 30px;
      z-index: 1;
      opacity: 0.4;
    }
  }
}
</style>
